import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, doc, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { HiOutlineBookOpen, HiOutlineUsers, HiOutlineTrash } from 'react-icons/hi2';
import useRole from '../../hooks/useRole';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PageHeader from '../../components/common/PageHeader';
import { toLocalDateStr } from '../../utils/dateFormat';
const CLASS_COLORS = ['#4A7CFE', '#22C55E', '#F59E0B', '#EF4444', '#A855F7', '#06B6D4', '#F97316', '#EC4899'];

const ClassListPage = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const { canEdit, isStudent, isParent } = useRole();
  const isChildView = isStudent || isParent;
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '', subject: '', room: '', color: '#4A7CFE', openDate: toLocalDateStr(new Date()),
  });

  useEffect(() => {
    if (!userData?.uid) return;
    loadClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.uid]);

  const loadClasses = async () => {
    try {
      const uid = userData.uid;
      const lookupUid = isParent ? (userData.children?.[0] || uid) : uid;
      const q = isChildView
        ? query(collection(db, 'classes'), where('studentIds', 'array-contains', lookupUid))
        : query(collection(db, 'classes'), where('teacherId', '==', uid));
      const snap = await getDocs(q);
      setClasses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('클래스 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setForm({ name: '', subject: '', room: '', color: '#4A7CFE', openDate: toLocalDateStr(new Date()), attendanceMode: 'teacher' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name) return;
    const data = {
      name: form.name,
      subject: form.subject,
      teacherId: userData.uid,
      teacherName: userData.name || '',
      room: form.room,
      color: form.color,
      attendanceMode: form.attendanceMode || 'teacher',
      studentIds: [],
      students: 0,
      createdAt: Timestamp.fromDate(new Date(form.openDate + 'T00:00:00')),
    };
    const id = `class-${Date.now()}`;
    await setDoc(doc(db, 'classes', id), data);
    setShowModal(false);
    loadClasses();
  };

  const handleDelete = async (e, cls) => {
    e.stopPropagation();
    const studentCount = cls.students || cls.studentIds?.length || 0;
    const msg = studentCount > 0
      ? `'${cls.name}' 클래스를 삭제하시겠습니까?\n\n⚠️ 수강 학생 ${studentCount}명의 클래스 정보도 함께 삭제됩니다.`
      : `'${cls.name}' 클래스를 삭제하시겠습니까?`;
    if (!window.confirm(msg)) return;
    if (!window.confirm('정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;
    await deleteDoc(doc(db, 'classes', cls.id));
    loadClasses();
  };

  return (
    <Container>
      <PageHeader title="내 클래스" showBack={false} />

      <Content>
        {canEdit && (
          <TopRow>
            <AddButton onClick={openCreate}>+ 새 클래스 만들기</AddButton>
          </TopRow>
        )}

        {loading ? (
          <LoadingSpinner />
        ) : classes.length === 0 ? (
          <EmptyText>{isChildView ? (isParent ? '자녀가 수강 중인 클래스가 없습니다.' : '수강 중인 클래스가 없습니다.') : '담당 클래스가 없습니다.'}</EmptyText>
        ) : (
          <ClassList>
            {classes.map((cls) => (
              <ClassCard key={cls.id} onClick={() => navigate(`/class/${cls.id}`)}>
                <ClassIcon $bg={cls.color || '#4A7CFE'}>
                  <HiOutlineBookOpen size={22} color="white" />
                </ClassIcon>
                <ClassInfo>
                  <ClassNameRow>
                    <ClassName>{cls.name}</ClassName>
                    {cls.subject && <ClassSubject>{cls.subject}</ClassSubject>}
                    {canEdit && (
                      <DeleteBtn onClick={(e) => handleDelete(e, cls)}>
                        <HiOutlineTrash size={16} />
                      </DeleteBtn>
                    )}
                  </ClassNameRow>
                  <ClassMeta>
                    {cls.schedule?.length > 0 && (
                      <ScheduleList>
                        {cls.schedule.map((s, i) => (
                          <ScheduleChip key={i}>{s.day} {s.time || s.startTime || ''}{s.endTime ? `~${s.endTime}` : ''}</ScheduleChip>
                        ))}
                      </ScheduleList>
                    )}
                    <ClassMetaBottom>
                      <StudentCount>
                        <HiOutlineUsers size={14} />
                        {cls.studentIds?.length || 0}명
                      </StudentCount>
                      <AttMode $self={cls.attendanceMode === 'self'}>
                        {cls.attendanceMode === 'self' ? '위치 기반' : '강사 체크'}
                      </AttMode>
                    </ClassMetaBottom>
                  </ClassMeta>
                </ClassInfo>
              </ClassCard>
            ))}
          </ClassList>
        )}
      </Content>

      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>클래스 추가</ModalTitle>
              <CloseBtn onClick={() => setShowModal(false)}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <Label>클래스명 *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder=""
              />

              <Label>과목</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder=""
              />

              <Label>개설일 *</Label>
              <Input
                type="date"
                value={form.openDate}
                onChange={(e) => setForm({ ...form, openDate: e.target.value })}
              />

              <Label>강의실</Label>
              <Input
                value={form.room}
                onChange={(e) => setForm({ ...form, room: e.target.value })}
                placeholder=""
              />

              <Label>출석 모드 *</Label>
              <AttModeRow>
                <AttModeBtn
                  type="button"
                  $active={form.attendanceMode === 'teacher'}
                  onClick={() => setForm({ ...form, attendanceMode: 'teacher' })}
                >강사 체크</AttModeBtn>
                <AttModeBtn
                  type="button"
                  $active={form.attendanceMode === 'self'}
                  onClick={() => setForm({ ...form, attendanceMode: 'self' })}
                >위치 기반</AttModeBtn>
              </AttModeRow>

              <Label>색상</Label>
              <ColorGrid>
                {CLASS_COLORS.map((c) => (
                  <ColorBtn
                    key={c}
                    type="button"
                    $color={c}
                    $active={form.color === c}
                    onClick={() => setForm({ ...form, color: c })}
                  />
                ))}
              </ColorGrid>

              <ModalActions>
                <CancelBtn onClick={() => setShowModal(false)}>취소</CancelBtn>
                <SaveBtn onClick={handleSave} disabled={!form.name}>추가</SaveBtn>
              </ModalActions>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ClassListPage;

const Container = styled.div`
  background: ${({ theme }) => theme.colors.background.default};
  min-height: 100vh;
  padding-bottom: 80px;
`;

const Content = styled.div`
  padding: 16px;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xl};
`;

const AddButton = styled.button`
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: var(--color-primary);
  color: white;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
`;

const ClassList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ClassCard = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadow.small};
  text-align: left;
`;

const ClassIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ $bg }) => $bg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ClassInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ClassNameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ClassName = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  flex: 1;
`;

const ClassMeta = styled.div`
  margin-top: 2px;
`;

const ClassMetaBottom = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
`;

const ClassSubject = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.hint};
  flex-shrink: 0;
`;

const ClassSchedule = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 2px;
`;

const ScheduleList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const ScheduleChip = styled.span`
  font-size: 11px;
  color: #374151;
  background: #F3F4F6;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
`;


const AttMode = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ $self }) => $self ? '#22C55E' : 'var(--color-primary)'};
  background: ${({ $self }) => $self ? '#E8F5E9' : 'var(--color-primary-light)'};
  padding: 2px 8px;
  border-radius: 4px;
  margin-top: 4px;
  display: inline-block;
`;

const StudentCount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.hint};
`;

const DeleteBtn = styled.div`
  padding: 4px;
  color: #EF4444;
  border-radius: 6px;
  flex-shrink: 0;
  &:hover { background: #FEF2F2; }
`;

const EmptyText = styled.p`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => theme.colors.text.hint};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 480px;
  background: white;
  border-radius: 20px 20px 0 0;
  max-height: 85vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--color-primary-light);
  border-bottom: 1px solid #D9E2FF;
`;

const ModalTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: #1E3A5F;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.text.primary};
  background: white;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  margin-bottom: 6px;
  margin-top: 14px;
  &:first-child { margin-top: 0; }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const AttModeRow = styled.div`
  display: flex;
  gap: 10px;
`;

const AttModeBtn = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.border};
  background: ${({ $active }) => $active ? 'var(--color-primary-light)' : 'white'};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ $active }) => $active ? 600 : 400};
`;

const ColorGrid = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 4px;
`;

const ColorBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  border: 3px solid ${({ $active, $color }) => $active ? $color : 'transparent'};
  outline: ${({ $active }) => $active ? '2px solid #E5E7EB' : 'none'};
  outline-offset: 1px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const SaveBtn = styled.button`
  flex: 2;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  &:disabled { opacity: 0.5; }
`;
